import { useCallback, useState } from 'react'
import { getGlobalSentry } from '../utils/init'

/**
 * 异步错误边界 Hook
 * 用于在 React 组件中捕获和处理异步错误
 */
export function useAsyncErrorBoundary() {
  const [error, setError] = useState<Error | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  /**
   * 捕获异步错误
   */
  const captureAsyncError = useCallback((error: Error, context?: Record<string, any>) => {
    const sentry = getGlobalSentry()
    if (sentry) {
      sentry.captureException(error, {
        context: 'async_error',
        ...context,
      })
    }
    setError(error)
  }, [])

  /**
   * 重置错误状态
   */
  const resetError = useCallback(() => {
    setError(null)
  }, [])

  /**
   * 包装异步函数，自动捕获错误
   */
  const withErrorHandling = useCallback(<T extends any[], R>(
    fn: (...args: T) => Promise<R>,
    context?: Record<string, any>,
  ) => {
    return async (...args: T): Promise<R | undefined> => {
      try {
        setIsLoading(true)
        setError(null)
        const result = await fn(...args)
        return result
      }
      catch (error) {
        captureAsyncError(error as Error, {
          functionName: fn.name || 'anonymous',
          arguments: args,
          ...context,
        })
        return undefined
      }
      finally {
        setIsLoading(false)
      }
    }
  }, [captureAsyncError])

  /**
   * 包装异步函数，静默捕获错误（返回默认值）
   */
  const withSilentErrorHandling = useCallback(<T extends any[], R>(
    fn: (...args: T) => Promise<R>,
    fallback: R,
    context?: Record<string, any>,
  ) => {
    return async (...args: T): Promise<R> => {
      try {
        setIsLoading(true)
        setError(null)
        const result = await fn(...args)
        return result
      }
      catch (error) {
        captureAsyncError(error as Error, {
          functionName: fn.name || 'anonymous',
          arguments: args,
          fallback,
          ...context,
        })
        return fallback
      }
      finally {
        setIsLoading(false)
      }
    }
  }, [captureAsyncError])

  return {
    error,
    isLoading,
    captureAsyncError,
    resetError,
    withErrorHandling,
    withSilentErrorHandling,
  }
}

export default useAsyncErrorBoundary
